import React, { useEffect } from 'react';
import { useSocket } from '../contexts/socketContext';
import { useAppDispatch } from '../store/hooks';
import { addNotification, setUnreadCount } from '../features/notifications/slices/notifications.slice';
import { fetchUnreadCount } from '../features/notifications/thunks/notifications.thunk';
import {
  fetchConversations,
  CHAT_CONVERSATIONS_PAGE_LIMIT,
} from '../features/chat/thunks/chat.thunk';
import { Notification } from '../features/notifications/interfaces/notifications.interface';
import { useNotificationToast } from '../contexts/NotificationToastContext';
import { formatNotificationData } from '../utils/notification.utils';

const NotificationSocketListener: React.FC = () => {
  const socket = useSocket();
  const dispatch = useAppDispatch();
  const { showNotification } = useNotificationToast();

  useEffect(() => {
    if (!socket) return;

    const formatTime = (date: Date) =>
      date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
      
    const getDayLabel = (date: Date) => {
      const d = new Date(date);
      const today = new Date();
      if (d.toDateString() === today.toDateString()) {
        return 'Today';
      }
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      if (d.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
      }
      return d.toLocaleDateString();
    };

    const onNotification = (payload: any) => {
      const formatted = formatNotificationData([payload])[0];
      dispatch(addNotification(formatted));
    };

    const onNewMessageNotification = (payload: any) => {
      const created = payload.createdAt ? new Date(payload.createdAt) : new Date();
      const actor = payload.actor ?? payload._actor ?? payload.meta?.actor ?? (payload.senderImage ? {
        id: payload.senderId || payload.userId,
        firstName: payload.senderName?.split(' ')[0] || '',
        lastName: payload.senderName?.split(' ').slice(1).join(' ') || '',
        profileImage: payload.senderImage || payload.senderAvatar
      } : null);

      const normalized: Notification = {
        id: payload.id || `tmp-${Date.now()}-${Math.random()}`,
        day: getDayLabel(created),
        time: payload.time || formatTime(created),
        title: payload.senderName || payload.title || 'Someone',
        body: payload.preview || payload.body || '',
        meta: {
          conversationId: payload.conversationId,
          messageId: payload.messageId,
          senderImage: payload.senderImage || payload.senderAvatar,
          preview: payload.preview,
          messageType: payload.messageType,
          reaction: payload.reaction,
        },
        type: 'NEW_MESSAGE',
        isRead: false,
        actor
      };

      dispatch(addNotification(normalized));
      dispatch(
        fetchConversations({
          page: 1,
          limit: CHAT_CONVERSATIONS_PAGE_LIMIT,
          append: false,
        }),
      );
    };

    const onNewProductNotification = (payload: any) => {
      const created = new Date();
      const isOwnListing = payload.sellerId === payload.userId;
      const productTitle = payload.productTitle || 'a new product';
      const normalized: Notification = {
        id: payload.id || `tmp-${Date.now()}-${Math.random()}`,
        day: getDayLabel(created),
        time: payload.time || formatTime(created),
        title: isOwnListing ? 'Your listing has been posted' : (payload.sellerName || 'A seller'),
        body: isOwnListing
          ? `"${productTitle}" is now live on the marketplace.`
          : `just listed "${productTitle}"`,
        message: isOwnListing
          ? `Your listing "${productTitle}" is now available on the marketplace`
          : `just listed "${productTitle}"`,
        text: isOwnListing ? 'Your listing has been posted,' : undefined,
        additionalText: isOwnListing
          ? `"${productTitle}" is now live on the marketplace.`
          : undefined,
        meta: {
          productId: payload.productId,
          productTitle: payload.productTitle,
          productImage: payload.productImage,
          sellerId: payload.sellerId,
          sellerName: payload.sellerName,
          sellerImage: isOwnListing ? null : payload.sellerImage
        },
        type: 'product',
        isRead: false,
        createdAt: created.toISOString(),
        actor: isOwnListing ? undefined : (payload.sellerImage ? {
          id: payload.sellerId,
          firstName: payload.sellerName?.split(' ')[0] || '',
          lastName: payload.sellerName?.split(' ').slice(1).join(' ') || '',
          profileImage: payload.sellerImage
        } : undefined),
        sellerImage: isOwnListing ? null : payload.sellerImage
      };

      const formatted = formatNotificationData([normalized])[0];
      dispatch(addNotification(formatted));
    };

    const onProductStatusChange = (payload: any) => {
      const created = new Date();
      const statusMessage = payload.previousStatus === 'PUBLISHED' && payload.newStatus === 'DRAFT'
        ? `Your listing "${payload.productTitle || 'a product'}" has been moved to drafts`
        : `Your listing "${payload.productTitle || 'a product'}" status changed from ${payload.previousStatus} to ${payload.newStatus}`;

      const normalized: Notification = {
        id: payload.id || `tmp-${Date.now()}-${Math.random()}`,
        day: getDayLabel(created),
        time: payload.time || formatTime(created),
        title: 'Status Change',
        body: statusMessage,
        meta: {
          productId: payload.productId,
          productTitle: payload.productTitle,
          previousStatus: payload.previousStatus,
          newStatus: payload.newStatus
        },
        type: 'product_status_change',
        isRead: false,
        createdAt: created.toISOString()
      };

      if (payload.previousStatus === 'PUBLISHED' && payload.newStatus === 'DRAFT') {
        showNotification({
          type: 'app',
          mainText: 'Listing moved to drafts',
          subText: `"${payload.productTitle || 'Your listing'}" has been moved to drafts`,
          duration: 6000
        });
      }

      dispatch(addNotification(normalized));
    };

    const onNotificationCount = (payload: any) => {
      const count = (payload && (
        payload.unreadCount ?? 
        payload.notificationCount ??
        payload.totalUnread ??
        payload.total ?? 
        payload.count ?? 
        payload.unread_count ?? 
        payload.total_unread ?? 
        payload.data?.unreadCount
      )) as number | undefined;
      
      if (typeof count === 'number') {
        dispatch(setUnreadCount(count));
      }
    };


    const onNotificationCountUpdated = () => {
      dispatch(fetchUnreadCount());
    };

    const onMarkReadSuccess = () => {
      dispatch(fetchUnreadCount());
    };

    socket.on('notification', onNotification);
    socket.on('new_message_notification', onNewMessageNotification);
    socket.on('new_product_notification', onNewProductNotification);
    socket.on('product_status_change', onProductStatusChange);
    socket.on('notification_count', onNotificationCount);
    socket.on('notification_count_updated', onNotificationCountUpdated);
    
    // Also listen to chat events that trigger notification state changes
    socket.on('messages_read', onNotificationCountUpdated);
    socket.on('conversation_read', onNotificationCountUpdated);
    socket.on('message_status_updated', (data: any) => {
      if (data?.status === 'READ' || data?.status === 'read') {
        onNotificationCountUpdated();
      }
    });

    return () => {
      socket.off('notification', onNotification);
      socket.off('new_message_notification', onNewMessageNotification);
      socket.off('new_product_notification', onNewProductNotification);
      socket.off('product_status_change', onProductStatusChange);
      socket.off('notification_count', onNotificationCount);
      socket.off('notification_count_updated', onNotificationCountUpdated);
      socket.off('messages_read', onNotificationCountUpdated);
      socket.off('conversation_read', onNotificationCountUpdated);
    };

  }, [socket, dispatch, showNotification]);

  // Periodic background check as a safety fallback
  useEffect(() => {
    const interval = setInterval(() => {
      if (socket && socket.connected) {
        dispatch(fetchUnreadCount());
      }
    }, 60000); // Check once per minute

    return () => clearInterval(interval);
  }, [socket, dispatch]);

  return null;
};


export default NotificationSocketListener;
